import React, { useState } from 'react';
import { toast } from 'sonner';
import { CopyIcon, FileSpreadsheetIcon, FlaskConicalIcon, LockIcon, PencilLineIcon, ShieldCheckIcon } from 'lucide-react';
import { changeSummaryV2, employeeSettings, initialAuditLog, planVersions, validationByVersion } from '../data/compPlan';
import { currentUser, ruleEditorPermission, ruleEditors } from '../data/people';
import { Badge } from '../components/sf/Badge';
import { Button } from '../components/sf/Button';
import { NewBadge } from '../components/sf/NewBadge';
import { HighlightField, RecordHeader } from '../components/sf/RecordHeader';
import { ScopedNotification } from '../components/sf/ScopedNotification';
import { SelectField } from '../components/sf/SelectField';
import { StatusBadge } from '../components/sf/StatusBadge';
import { Tabs } from '../components/sf/Tabs';
import { Tooltip } from '../components/sf/Tooltip';
import { AuditLogTable } from '../components/compPlan/AuditLogTable';
import { ChangeSummaryCard } from '../components/compPlan/ChangeSummaryCard';
import { EmployeeRulesCard } from '../components/compPlan/EmployeeRulesCard';
import { LoadDefaultsModal } from '../components/compPlan/LoadDefaultsModal';
import { PrecedenceCard } from '../components/compPlan/PrecedenceCard';
import { RulesTable } from '../components/compPlan/RulesTable';
import { ValidationCard } from '../components/compPlan/ValidationCard';
import { VersionHistoryTable } from '../components/compPlan/VersionHistoryTable';
import { AuditEntry, EmployeeSetting } from '../types/commission';
type PlanTab = 'rules' | 'audit' | 'history';
interface CompPlanAdminProps {
  canEditRules: boolean;
}
export function CompPlanAdmin({
  canEditRules
}: CompPlanAdminProps) {
  const [versionNumber, setVersionNumber] = useState(1);
  const [tab, setTab] = useState<PlanTab>('rules');
  const [auditLog, setAuditLog] = useState<AuditEntry[]>(initialAuditLog);
  const [isLoadOpen, setIsLoadOpen] = useState(false);
  const version = planVersions.find(item => item.version === versionNumber) ?? planVersions[0];
  const isDraft = version.status === 'Draft';
  const editable = isDraft && canEditRules;
  const viewer = canEditRules ? currentUser : 'Read-only user';
  const editorsLabel = ruleEditors.join(' and ');
  const readOnlyReason = !canEditRules ? `Only ${editorsLabel} (${ruleEditorPermission}) can edit rules.` : 'Active versions are read-only. Clone to a new version to change rates.';
  const fields: HighlightField[] = [{
    label: 'Version',
    value: version.label
  }, {
    label: 'Effective Start',
    value: version.effectiveStartLabel
  }, {
    label: 'Effective End',
    value: version.effectiveEndLabel
  }, {
    label: 'Rules',
    value: String(version.rules.length)
  }, {
    label: 'Rule Editors',
    value: `${ruleEditors.join(', ')}`
  }, {
    label: 'Permission',
    value: ruleEditorPermission
  }];
  const addAudit = (entries: AuditEntry[]) => setAuditLog(prev => [...entries, ...prev]);
  const handleToggleBonus = (setting: EmployeeSetting, bonusOn: boolean) => {
    addAudit([{
      id: `audit-${Date.now()}-${setting.id}`,
      when: 'Just now',
      who: viewer,
      ruleKey: `V${version.version}-EMP-${setting.name.split(' ')[1]?.toUpperCase() ?? setting.id}`,
      field: 'Monthly Bonus switch',
      from: bonusOn ? 'Off' : 'On',
      to: bonusOn ? 'On' : 'Off',
      reason: 'Employee rule toggled in Draft'
    }]);
    toast.success(`Monthly Bonus turned ${bonusOn ? 'on' : 'off'} for ${setting.name}`, {
      description: 'Logged to the audit log.'
    });
  };
  const handleClone = () => toast.success(`Cloned ${version.label} to v3 Draft`, {
    description: 'All rules copied with new V3 rule keys. Set an effective period to continue.'
  });
  const handleLoad = (includeSampleData: boolean) => {
    setIsLoadOpen(false);
    toast.success('Default rules loaded from 2026-v1-parity', {
      description: includeSampleData ? 'Sample LOs, loans and an October 2026 period were added.' : 'Rules only — no sample data.'
    });
  };
  return <div className="space-y-3" data-id="element-1078" data-mp="27v7lbsqgcqu5">
      <RecordHeader objectLabel="Comp Plan" title="LO Standard Plan" icon={FileSpreadsheetIcon} iconBg="#F49756" titleAddon={<>
            <SelectField id="plan-version" label="Plan version" hideLabel value={String(versionNumber)} options={planVersions.map(item => ({
        value: String(item.version),
        label: `${item.label} · ${item.status}`
      }))} onChange={value => setVersionNumber(Number(value))} data-id="element-1080" data-mp="2htwyj87eln42" />
            <StatusBadge status={version.status} data-id="element-1081" data-mp="30k4wzd05rpdx" />
            <NewBadge label="NEW · plan versions & effective dates" data-id="element-1082" data-mp="30jlh01u2jb2j" />
          </>} actions={<>
            <span className="inline-flex items-center gap-1 rounded-full border border-[#F9C88C] bg-sf-amber-light px-2 py-0.5 text-[11px] font-semibold text-sf-amber-text" data-id="element-1083" data-mp="35h4wf52i5tw2">
              <FlaskConicalIcon className="h-3 w-3" aria-hidden="true" data-id="element-1084" data-mp="xz10f7xkiovx" />
              Sandbox
            </span>
            <NewBadge label="NEW" data-id="element-1085" data-mp="tukvb8vs3lhr" />
            {canEditRules ? <Button onClick={() => setIsLoadOpen(true)} data-id="element-1086" data-mp="oxudoh4bshu8">Load Default Rules</Button> : <Tooltip content={readOnlyReason} side="bottom" data-id="element-1087" data-mp="jyq4vrlbcm4f">
                <Button disabled data-id="element-1088" data-mp="1hl71eum82vp4">Load Default Rules</Button>
              </Tooltip>}
            <Tooltip content={isDraft ? 'Effective period is TBD. Set a start date before activating.' : `${version.label} is already the Active version.`} side="bottom" data-id="element-1089" data-mp="1cmm16v6z0c0o">
              <Button disabled data-id="element-1090" data-mp="12x2jvtywoq7x">Activate</Button>
            </Tooltip>
            {canEditRules ? <Button variant="brand" icon={<CopyIcon data-id="element-1092" data-mp="20cq4f1g8y9r5" />} onClick={handleClone} data-id="element-1091" data-mp="20cgf7g3106mi">
                Clone to New Version
              </Button> : <Tooltip content={readOnlyReason} side="bottom" align="end" data-id="element-1093" data-mp="1vj54h3v3f5pb">
                <Button variant="brand" icon={<CopyIcon data-id="element-1095" data-mp="3pvg05je7939r" />} disabled data-id="element-1094" data-mp="3ptln0r966fg8">
                  Clone to New Version
                </Button>
              </Tooltip>}
          </>} fields={fields} data-id="element-1079" data-mp="27w8t9oc68x7n" />

      <div className="grid grid-cols-1 items-start gap-3 lg:grid-cols-[minmax(0,1fr)_320px]" data-id="element-1096" data-mp="3g1667x9lzwwx">
        <div className="space-y-3" data-id="element-1097" data-mp="3l0914ovvkmr6">
          <section className="rounded border border-sf-border bg-white shadow-card" data-id="element-1098" data-mp="364l17bxlalt3">
            <div className="flex items-center justify-between border-b border-sf-border pr-4" data-id="element-1099" data-mp="3b45x1dhgqe1l">
              <div className="flex-1 [&>div]:border-b-0" data-id="element-1100" data-mp="2x4egp63h4lkf">
                <Tabs label="Plan sections" tabs={[{
                id: 'rules',
                label: `Comp Rules (${version.rules.length})`
              }, {
                id: 'audit',
                label: `Audit Log (${auditLog.length})`
              }, {
                id: 'history',
                label: 'Version History'
              }]} active={tab} onChange={setTab} data-id="element-1101" data-mp="2x5zbmwtpa6it" />
              </div>
              <Badge tone={canEditRules ? 'successLight' : 'neutral'} icon={canEditRules ? <ShieldCheckIcon className="h-3 w-3" aria-hidden="true" data-id="element-1103" data-mp="15evkhbw0bhah" /> : <LockIcon className="h-3 w-3" aria-hidden="true" data-id="element-1104" data-mp="1j9eplv3c4ywh" />} data-id="element-1102" data-mp="15gylss2m0wui">
                {canEditRules ? `${viewer} · ${ruleEditorPermission}` : `${viewer} · Read-only`}
              </Badge>
            </div>
            <div className="space-y-3 p-4" data-id="element-1105" data-mp="1t5ho8qyzafeo">
              {tab === 'rules' && <>
                  {!canEditRules ? <ScopedNotification tone="info" icon={LockIcon} data-id="element-1106" data-mp="1o9u1ei7yxycc">
                      Read-only. Only {editorsLabel} ({ruleEditorPermission}) can edit commission rules.
                    </ScopedNotification> : isDraft ? <ScopedNotification tone="warning" icon={PencilLineIcon} data-id="element-1107" data-mp="7u9atspxj9wi">
                      <strong data-id="element-1108" data-mp="3s4iqyf4qlbz">Draft — editable.</strong> Click any Min, Max or Value to edit. Saving requires a reason. Effective period: TBD.
                    </ScopedNotification> : <ScopedNotification tone="info" icon={LockIcon} action={<Button size="sm" onClick={handleClone} data-id="element-1110" data-mp="1235qvsncvjmw">
                          Clone to New Version
                        </Button>} data-id="element-1109" data-mp="xauhlz6q6lho">
                      Active versions can’t be edited. Clone to a new version to change rates.
                    </ScopedNotification>}
                  <RulesTable key={version.version} version={version} editable={editable} readOnlyReason={readOnlyReason} editor={viewer} onAudit={addAudit} data-id="element-1111" data-mp="idveoomtjgtj" />
                </>}
              {tab === 'audit' && <AuditLogTable entries={auditLog} data-id="element-1112" data-mp="1tubkdq8kmj37" />}
              {tab === 'history' && <VersionHistoryTable versions={planVersions} data-id="element-1113" data-mp="1ysmywe9t9hrr" />}
            </div>
          </section>
          {tab === 'rules' && <EmployeeRulesCard key={version.version} settings={employeeSettings} editable={editable} onToggleBonus={handleToggleBonus} data-id="element-1114" data-mp="28x39as4iu6o6" />}
        </div>

        <aside className="space-y-3" aria-label="Plan checks" data-id="element-1115" data-mp="23y8qvraguk4l">
          <ValidationCard title="Validation" checks={validationByVersion[version.version] ?? []} data-id="element-1116" data-mp="1z5p4q5xpqgo6" />
          {isDraft && <ChangeSummaryCard changes={changeSummaryV2} data-id="element-1117" data-mp="1v4dous4n59t6" />}
          <PrecedenceCard data-id="element-1118" data-mp="1q5snuqhkem3o" />
        </aside>
      </div>

      <LoadDefaultsModal open={isLoadOpen} onClose={() => setIsLoadOpen(false)} onLoad={handleLoad} data-id="element-1119" data-mp="1q7dh8cs57yzy" />
    </div>;
}